import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Alert from '../components/ui/Alert';

// Load Google Identity Services script
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function SignInPage() {
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: string })?.from || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'filled_blue',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 300,
          });
        }
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleCredentialResponse = async (response: any) => {
    try {
      setError(null);
      const credential = response.credential;

      if (!credential) {
        setError('Failed to get Google credential. Please try again.');
        return;
      }

      // Call backend to exchange Google credential for app token
      await signIn(credential);
      
      // Navigation will be handled by the useEffect above
    } catch (err: any) {
      const message =
        err.response?.data?.error || 
        err.message ||
        'Authentication failed. Please try again.';
      setError(message);
    }
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="signin-page">
      <div className="signin-card">
        <div className="signin-header">
          <h1>Hospital AR/AP</h1>
          <h2>Calculation System</h2>
          <p>Sign in to access the system</p>
        </div>

        {error && (
          <Alert type="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="signin-actions">
          <div id="google-signin-button"></div>
        </div>

        <div className="signin-footer">
          <p className="signin-note">
            Sign in with your @pristyncare.com Google account
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignInPage;
